
if (!customElements.get('testimonial-slider')) {
  class TestimonialSlider extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.viewport = this.querySelector('.cts-viewport');
      this.track = this.querySelector('.cts-track');
      this.previous = this.querySelector('.cts-arrow--previous');
      this.next = this.querySelector('.cts-arrow--next');
      this.pagination = this.querySelector('.cts-pagination');

      if (!this.viewport || !this.track) return;

      this.initialized = true;

      this.reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      );

      this.onScroll = () => this.updateControls();

      this.onPrevious = () => {
        this.goToPage(this.getCurrentPage() - 1);
      };

      this.onNext = () => {
        this.goToPage(this.getCurrentPage() + 1);
      };

      this.onPaginationClick = (event) => {
        const dot = event.target.closest('[data-page]');

        if (!dot || !this.pagination?.contains(dot)) return;

        this.goToPage(Number(dot.dataset.page));
      };

      this.onBlockSelect = (event) => {
        const card = event.target.closest('.cts-card');

        if (!card || !this.contains(card)) return;

        const index = this.getCards().indexOf(card);

        if (index !== -1) {
          this.goToCard(index);
        }
      };

      this.viewport.addEventListener('scroll', this.onScroll, {
        passive: true
      });

      this.previous?.addEventListener('click', this.onPrevious);
      this.next?.addEventListener('click', this.onNext);

      this.pagination?.addEventListener(
        'click',
        this.onPaginationClick
      );

      this.addEventListener(
        'shopify:block:select',
        this.onBlockSelect
      );

      this.resizeObserver = new ResizeObserver(() => {
        this.refresh();
      });

      this.resizeObserver.observe(this.viewport);

      this.mutationObserver = new MutationObserver(() => {
        this.refresh();
      });

      this.mutationObserver.observe(this.track, {
        childList: true
      });

      this.refresh();
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      this.viewport.removeEventListener(
        'scroll',
        this.onScroll
      );

      this.previous?.removeEventListener(
        'click',
        this.onPrevious
      );

      this.next?.removeEventListener(
        'click',
        this.onNext
      );

      this.pagination?.removeEventListener(
        'click',
        this.onPaginationClick
      );

      this.removeEventListener(
        'shopify:block:select',
        this.onBlockSelect
      );

      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();

      this.initialized = false;
    }

    /* Slides
       ==================================================================== */

    getCards() {
      return [
        ...this.track.querySelectorAll(':scope > .cts-card')
      ];
    }

    getMaximumScroll() {
      return Math.max(
        0,
        this.viewport.scrollWidth - this.viewport.clientWidth
      );
    }

    getCardPosition(card) {
      const firstCard = this.getCards()[0];

      if (!firstCard || !card) return 0;

      return Math.max(
        0,
        card.offsetLeft - firstCard.offsetLeft
      );
    }

    /* Pagination Positions
       ==================================================================== */

    getPagePositions() {
      const cards = this.getCards();
      const max = this.getMaximumScroll();

      if (!cards.length || max <= 1) return [0];

      const positions = [0];

      cards.forEach((card) => {
        const position = Math.min(
          max,
          this.getCardPosition(card)
        );

        const last = positions[positions.length - 1];

        if (position - last > 2) {
          positions.push(position);
        }
      });

      const last = positions[positions.length - 1];

      if (max - last > 2) {
        positions.push(max);
      }

      return positions;
    }

    getCurrentPage() {
      const positions = this.getPagePositions();
      const scroll = this.viewport.scrollLeft;

      let closestIndex = 0;
      let closestDistance = Infinity;

      positions.forEach((position, index) => {
        const distance = Math.abs(scroll - position);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    /* Navigation
       ==================================================================== */

    scrollToPosition(position) {
      this.viewport.scrollTo({
        left: Math.max(
          0,
          Math.min(position, this.getMaximumScroll())
        ),
        behavior: this.reducedMotion.matches ? 'instant' : 'smooth'
      });
    }

    goToPage(page) {
      const positions = this.getPagePositions();

      const index = Math.max(
        0,
        Math.min(page, positions.length - 1)
      );

      this.scrollToPosition(positions[index]);
    }

    goToCard(index) {
      const cards = this.getCards();
      const card = cards[index];

      if (!card) return;

      this.scrollToPosition(this.getCardPosition(card));
    }

    /* Pagination
       ==================================================================== */

    renderPagination() {
      if (!this.pagination) return;

      const count = this.getPagePositions().length;

      this.pagination.replaceChildren();
      this.pagination.hidden = count <= 1;

      if (count <= 1) return;

      const fragment = document.createDocumentFragment();

      for (let index = 0; index < count; index++) {
        const dot = document.createElement('button');

        dot.type = 'button';
        dot.className = 'cts-dot';
        dot.dataset.page = index;

        dot.setAttribute(
          'aria-label',
          `Go to testimonial page ${index + 1}`
        );

        fragment.appendChild(dot);
      }

      this.pagination.appendChild(fragment);
    }

    updateControls() {
      const max = this.getMaximumScroll();
      const current = this.getCurrentPage();
      const hasOverflow = max > 1;

      if (this.previous) {
        this.previous.hidden = !hasOverflow;

        this.previous.disabled =
          !hasOverflow || this.viewport.scrollLeft <= 2;
      }

      if (this.next) {
        this.next.hidden = !hasOverflow;

        this.next.disabled =
          !hasOverflow ||
          this.viewport.scrollLeft >= max - 2;
      }

      this.pagination
        ?.querySelectorAll('.cts-dot')
        .forEach((dot, index) => {
          dot.setAttribute(
            'aria-current',
            index === current ? 'true' : 'false'
          );
        });
    }

    /* Shopify Theme Editor
       ==================================================================== */

    refresh() {
      const cards = this.getCards();

      cards.forEach((card, index) => {
        card.setAttribute('role', 'group');
        card.setAttribute('aria-roledescription', 'slide');

        card.setAttribute(
          'aria-label',
          `Testimonial ${index + 1} of ${cards.length}`
        );
      });

      this.renderPagination();
      this.updateControls();
    }
  }

  customElements.define(
    'testimonial-slider',
    TestimonialSlider
  );
}
